import { useEffect, useState, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PAGE, BTN_PRIMARY, BTN_GHOST, ICON_BTN, CARD } from "@/lib/styles";
import { Trash2, RotateCcw, FileText, Network, Loader2, Trash, Table2, Wallet, Landmark, Scale } from "lucide-react";
import { toast } from "sonner";

const KIND = {
  text: { label: "Nota", Icon: FileText, wrap: "bg-sky-500/10 text-sky-500 border-sky-500/20" },
  mindmap: { label: "Mapa", Icon: Network, wrap: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20" },
  sheet: { label: "Hoja", Icon: Table2, wrap: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  movemap: { label: "Flujo económico", Icon: Wallet, wrap: "bg-orange-500/10 text-orange-500 border-orange-500/20" },
  board: { label: "Pizarra", Icon: Landmark, wrap: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  ledger: { label: "Contable", Icon: Scale, wrap: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  pdf: { label: "PDF", Icon: FileText, wrap: "bg-rose-500/10 text-rose-500 border-rose-500/20" },
};

const daysLeft = (deletedAt) => {
  if (!deletedAt) return null;
  try {
    const diff = 30 - Math.floor((Date.now() - parseISO(deletedAt).getTime()) / 86400000);
    return Math.max(0, diff);
  } catch { return null; }
};

export default function Papelera() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, d] = await Promise.all([api.get("/notes/trash"), api.get("/docs/files/trash")]);
      const notes = n.data.map((x) => ({ kind: x.type, id: x.id, title: x.title, deleted_at: x.deleted_at }));
      const docs = d.data.map((x) => ({ kind: "pdf", id: x.id, title: x.filename, deleted_at: x.deleted_at }));
      setItems([...notes, ...docs].sort((a, b) => (b.deleted_at || "").localeCompare(a.deleted_at || "")));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (it) => {
    try {
      if (it.kind === "pdf") await api.post(`/docs/files/${it.id}/restore`);
      else await api.post(`/notes/${it.id}/restore`);
      setItems((arr) => arr.filter((x) => !(x.id === it.id && x.kind === it.kind)));
      toast.success("Restaurado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const purge = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.title}" para siempre? Esta acción no se puede deshacer.`)) return;
    try {
      if (it.kind === "pdf") await api.delete(`/docs/files/${it.id}/permanent`);
      else await api.delete(`/notes/${it.id}/permanent`);
      setItems((arr) => arr.filter((x) => !(x.id === it.id && x.kind === it.kind)));
      toast.success("Eliminado permanentemente");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const emptyAll = async () => {
    if (!items.length) return;
    if (!window.confirm(`¿Vaciar la papelera? Se eliminarán ${items.length} elemento(s) para siempre.`)) return;
    setBusy(true);
    for (const it of items) {
      try {
        if (it.kind === "pdf") await api.delete(`/docs/files/${it.id}/permanent`);
        else await api.delete(`/notes/${it.id}/permanent`);
      } catch { /* continue */ }
    }
    setBusy(false);
    setItems([]);
    toast.success("Papelera vaciada");
  };

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
              <Trash2 size={34} className="text-slate-400" /> Papelera
            </h1>
            <p className="font-mono2 text-xs text-slate-500 dark:text-slate-400 mt-2">
              Los elementos se eliminan automáticamente después de 30 días.
            </p>
          </div>
          {items.length > 0 && (
            <button onClick={emptyAll} disabled={busy} className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white transition-colors" data-testid="empty-trash">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash size={16} />} Vaciar papelera
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-16 text-center" data-testid="trash-empty">
            <Trash2 size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="font-heading font-normal text-xl mb-2">La papelera está vacía</h3>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400">Lo que borres aparecerá aquí y podrás restaurarlo.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="trash-list">
            {items.map((it) => {
              const meta = KIND[it.kind] || KIND.text;
              const Icon = meta.Icon;
              const left = daysLeft(it.deleted_at);
              return (
                <div key={`${it.kind}-${it.id}`} className={`${CARD} p-3.5 flex items-center gap-3`} data-testid={`trash-item-${it.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center border shrink-0 ${meta.wrap}`}><Icon size={17} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-medium text-sm truncate">{it.title}</p>
                    <p className="font-mono2 text-[10px] text-slate-400">
                      <span className={`uppercase ${meta.wrap} border px-1 py-0.5 rounded mr-2`}>{meta.label}</span>
                      {it.deleted_at ? `Borrado ${format(parseISO(it.deleted_at), "d 'de' MMMM yyyy", { locale: es })}` : "Borrado"}
                      {left !== null ? ` · se elimina en ${left} día(s)` : ""}
                    </p>
                  </div>
                  <button onClick={() => restore(it)} className={`${BTN_GHOST} py-1.5 text-xs`} data-testid={`restore-${it.id}`}>
                    <RotateCcw size={14} /> Restaurar
                  </button>
                  <button onClick={() => purge(it)} className={ICON_BTN} title="Eliminar para siempre" data-testid={`purge-${it.id}`}>
                    <Trash2 size={15} className="text-rose-500" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
